import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { KeyRound, Loader2, Eye, EyeOff } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/**
 * /onboarding/change-password
 *
 * Forced password-change screen for users whose account has
 * `must_change_password === true` (e.g. teachers freshly provisioned by an
 * admin with a one-time password). The user cannot skip this — every route
 * that gates on auth should redirect here as long as the flag is set.
 */
export default function ChangePasswordRequired() {
    const { user, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);

    useSEO({ title: 'Set your password | Linguistic Quest', path: '/onboarding/change-password', noindex: true });

    if (!user) return <Navigate to="/login" replace />;
    if (!user.must_change_password) return <Navigate to="/teacher" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (newPw.length < 8) { setErr('New password must be at least 8 characters.'); return; }
        if (newPw !== confirmPw) { setErr('The two new-password fields do not match.'); return; }
        setSubmitting(true);
        try {
            await api.post('/me/password', { current_password: currentPw, new_password: newPw });
            await refreshUser();
            toast.success('Password updated. Welcome to Linguistic Quest.');
            const role = (user.role || '').toLowerCase();
            navigate(role === 'teacher' ? '/teacher' : (role === 'admin' ? '/admin' : '/dashboard'), { replace: true });
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not update password');
        } finally { setSubmitting(false); }
    };

    return (
        <main className="max-w-md mx-auto px-6 py-12" data-testid="page-force-change-password">
            <div className="text-center">
                <div className="inline-flex h-14 w-14 rounded-full bg-lq-purple-100 items-center justify-center text-lq-purple-700">
                    <KeyRound size={28} />
                </div>
                <h1 className="mt-4 font-display text-3xl font-semibold text-lq-ink-black">Set your password</h1>
                <p className="mt-2 text-lq-ink-500">
                    Your administrator created your account with a one-time password.
                    Choose a new one to continue.
                </p>
            </div>

            <form onSubmit={submit} className="mt-8 bg-white border border-lq-ink-100 rounded-2xl p-6 space-y-4">
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">One-time password from the welcome email</span>
                    <div className="mt-1 relative">
                        <input
                            type={show ? 'text' : 'password'}
                            value={currentPw}
                            onChange={(e) => setCurrentPw(e.target.value)}
                            autoFocus
                            required
                            className="w-full rounded-lg border border-lq-ink-300 px-3 py-2 pr-10 font-mono"
                            data-testid="force-change-current"
                        />
                        <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-lq-ink-500 hover:text-lq-ink-900" aria-label={show ? 'Hide passwords' : 'Show passwords'}>
                            {show ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">New password (8+ characters)</span>
                    <input
                        type={show ? 'text' : 'password'}
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        minLength={8}
                        required
                        className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"
                        data-testid="force-change-new"
                    />
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Confirm new password</span>
                    <input
                        type={show ? 'text' : 'password'}
                        value={confirmPw}
                        onChange={(e) => setConfirmPw(e.target.value)}
                        minLength={8}
                        required
                        className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"
                        data-testid="force-change-confirm"
                    />
                </label>
                {err && <div className="text-sm text-lq-error" data-testid="force-change-error">{err}</div>}
                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="force-change-submit"
                >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    Save and continue
                </button>
                <button
                    type="button"
                    onClick={() => { logout(); navigate('/login', { replace: true }); }}
                    className="w-full py-2 text-sm text-lq-ink-500 hover:text-lq-ink-900"
                >
                    Sign out instead
                </button>
            </form>
        </main>
    );
}
